// Quintype domain - just the domain, /api/v1 is appended by code
// On BK deployments use internal URL: http://dinamani.internal.quintype.io
const QUINTYPE_DOMAIN = process.env.QUINTYPE_API_BASE_URL || "https://dinamani.quintype.io";

// API Base URL - domain + /api/v1 path for server-side rendering
export const API_BASE_URL = `${QUINTYPE_DOMAIN}/api/v1`;

// Canonical API base URL - always the public URL for consistent key normalization
// Server may use internal URL for direct access via QUINTYPE_API_BASE_URL env,
// but canonical URL ensures keys match between server-stored data and client lookups
export const API_CANONICAL_BASE_URL = "https://dinamani.quintype.io/api/v1";

// Log configuration on startup (server-side only)
if (typeof window === 'undefined') {

}
// Reverse proxy path for client-side requests (configured in next.config.ts)
export const API_PROXY_PATH = "/api/v1";
// Collections API base (for server-side)
export const API_COLLECTIONS_BASE = `${API_BASE_URL}/collections/`;
export const MEDIA_BASE_URL = process.env.MEDIA_BASE_URL || "https://media.dinamani.com";
export const VIDEO_BASE_URL = process.env.VIDEO_BASE_URL || "https://video.assettype.com";
export const PANCHANGAM_API_BASE_URL = process.env.PANCHANGAM_API_BASE_URL || "https://apifeeds.dinamani.com/panchangam/api";
export const WEBSITE_SETTINGS_BASE_URL = process.env.WEBSITE_SETTINGS_BASE_URL || "https://builderapi.dinamani.com/api/website-settings/public";
export const ADS_API_BASE_URL = process.env.ADS_API_BASE_URL || "https://builderapi.dinamani.com/api";

// Sitemap API Base URL
export const SITEMAP_API_BASE_URL = process.env.SITEMAP_API_BASE_URL || "https://dinamani.quintype.io";
export const SITEMAP_PROXY_PATH = "/api/sitemap";

export const SITEMAP_ENDPOINTS = {
  NEWS_SITEMAP: "/sitemap/sitemap.xml",
} as const;

// Fallback image
export const FALLBACK_IMAGE_PATH = process.env.FALLBACK_IMAGE_PATH || "dinamani/2024-02/86841f97-d91a-494c-aac7-192eaf085be9/fallback.jpeg";
export const FALLBACK_IMAGE = `${MEDIA_BASE_URL}/${FALLBACK_IMAGE_PATH}`;

/**
 * Validate if a string is a valid URL for images
 * Returns the URL if valid, or FALLBACK_IMAGE if invalid
 */
export function getValidImageUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string' || url.trim() === '') {
    return FALLBACK_IMAGE;
  }
  try {
    new URL(url);
    return url;
  } catch {
    return FALLBACK_IMAGE;
  }
}

// Site URL
export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://www.dinamani.com";

export const API_ENDPOINTS = {
  STORIES_BY_SLUG: "/stories-by-slug",
  RELATED_STORIES: "/stories",
  VIDEOS: "/collections/videos",
  SEARCH: "/search",
  ADVANCED_SEARCH: "/advanced-search",
  TRENDING: "/stories",
  TRENDING_TAGS: "/trending/tags",
  KARUTHUK_KALAM: "/collections/karuthuk-kalam-all-sections",
  GALLERIES: "/collections/galleries",
  COLLECTIONS: "/collections",
  MENU_GROUPS: "/menu-groups",
  STATIC_PAGES: "/static-pages",
} as const;

export const PANCHANGAM_ENDPOINTS = {
  PANCHANGAM_DATA: "/panchangam_data.php",
} as const;

export const ADS_ENDPOINTS = {
  PAGE_AD_SLOTS: "/page-ad-slots/public/multi",
  ARTICLE_AD: "/page-ad-slots/public/article-ad",
  SLIDER_AD: "/page-ad-slots/public/silder-ad",
} as const;

export const WEBSITE_SETTINGS_ENDPOINTS = {
  AD_SLOT_AFTER_PARAGRAPH: "/ad-slot-after-paragraph",
  AD_SLOT_AFTER_STORIES: "/ad-slot-after-stories",
  ARTICLE_PAGE_STYLE: "/article-page-style",
} as const;

// Section Page Ad Slot Names
export const SECTION_AD_SLOT_NAMES = {
  BOTTOM1: "section-bottom1",
  BOTTOM2: "section-bottom2",
  BIG_AD: "sectionpage-big-ad",
  SMALL_AD: "sectionpage-small-ad",
  INLINE_728x90: "section-page-ad-728x90",
} as const;

// Article Page Ad Slot Names
export const ARTICLE_AD_SLOT_NAMES = {
  BIG_AD_1: "article-page-big-ad-1",
  SMALL_1: "article-page-small-1",
  BIG_AD_2: "article-page-big-ad-2",
  MULTIPLE_300X250: "article-page-multiple-300x250-ad",
  BIG_AD_3: "article-page-big-ad-3",
  ROS_INREAD: "article-page-ros-inread",
  BOTTOM1: "article-bottom1",
  BOTTOM2: "article-bottom",
  FIRST_AD_MOBILE: "article-page-first-ad-mobile",
  INFINITE_AD: "article-page-infinite-ad",
} as const;

export const AMP_AD_SLOT_NAMES = {
  TOP: "amp-top",
  MID: "amp-mid",
  MID1: "amp-mid1",
  MID2: "amp-mid2",
  BTM: "amp-btm",
  STICKY: "amp-sticky",
} as const;

export const REVALIDATION_TIMES = {
  // No cache - fresh data on every request (for sensitive/dynamic news data)
  NONE: 0,             // No cache
  // For frequently changing data (trending, latest news)
  FREQUENT: 30,        // 30 seconds - for trending/breaking news
  // Default for most API calls (collections, stories) - instant render with background refresh
  DEFAULT: 60,         // 1 minute - stale-while-revalidate for fast page loads
  // For route config and page JSON
  RARE: 120,           // 2 minutes
  // For static data (menu, navigation, site config)
  STATIC: 1800,        // 30 minutes
} as const;

/**
 * CDN Cache Configuration (matching Quintype system)
 * - s-maxage: CDN cache TTL
 * - stale-while-revalidate: Serve stale while revalidating in background
 * - stale-if-error: Serve stale if origin returns error
 */
export const CDN_CACHE_CONFIG = {
  // Home page - short cache for fresh content
  HOME: {
    maxAge: 0,              // Browser cache
    sMaxAge: 120,           // CDN cache: 2 minutes
    staleWhileRevalidate: 300,   // Serve stale for 5 min while revalidating
    staleIfError: 7200,     // Serve stale for 2 hours on error
  },
  // Section/Collection pages
  SECTION: {
    maxAge: 0,
    sMaxAge: 300,           // CDN cache: 5 minutes
    staleWhileRevalidate: 600,
    staleIfError: 7200,
  },
  // Story/Article pages
  STORY: {
    maxAge: 0,
    sMaxAge: 600,           // CDN cache: 10 minutes
    staleWhileRevalidate: 1200,
    staleIfError: 7200,
  },
  // Static pages
  STATIC: {
    maxAge: 0,
    sMaxAge: 3600,          // CDN cache: 1 hour
    staleWhileRevalidate: 7200,
    staleIfError: 14400,
  },
  // API responses
  API: {
    maxAge: 15,             // Browser cache: 15 seconds
    sMaxAge: 240,           // CDN cache: 4 minutes (Quintype default)
    staleWhileRevalidate: 300,
    staleIfError: 7200,
  },
} as const;

/**
 * Publisher ID for cache tags
 */
export const PUBLISHER_ID = process.env.QUINTYPE_PUBLISHER_ID || '829';

export const AD_UNITS = {
  DSK_BTF: "DNM_Demo_DSK_BTF_728x90_970x90_970x250",
  DSK_STICKY: "DNM_Demo_DSK_Sticky_728x90",
  DSK_ATF: "DNM_Demo_DSK_ATF_300x250",
  DSK_RHS: "DNM_Demo_DSK_RHS_1600x600",
} as const;

export const SOCIAL_MEDIA_LINKS = {
  FACEBOOK: "https://facebook.com/dinamani",
  TWITTER: "https://twitter.com/dinamani",
  INSTAGRAM: "https://instagram.com/dinamani",
  YOUTUBE: "https://youtube.com/dinamani",
} as const;
